import { useMarket } from "@app/_context";
import { getCheckoutDelivery } from "@server/requests/checkout";
import { setDeliveryOption } from "@server/requests/delivery/setDeliveryOption";
import { useMutation, useQuery } from "@tanstack/react-query";

import { getCheckoutDeliveries } from "@/actions/getCheckoutDeliveries";
import { getCartCookie } from "@/utils/cart/getCartCookie";

import { useCheckoutSummary, useCheckoutZipCode } from "../Checkout.hooks";
import {
  type DeliveryOption,
  type DeliveryOptionImage,
  transformDeliveryOption,
} from "./Delivery.utils";

/**
 * Get nShift delivery options
 * This will be called when the user enters and submits an address with a zip code
 */
export function useCheckoutDelivery() {
  const { serviceLocale, locale } = useMarket().state.market;
  const zipCode = useCheckoutZipCode();
  const { cartTotal } = useCheckoutSummary();

  return useQuery({
    queryKey: ["checkoutDelivery", zipCode, cartTotal],
    queryFn: () =>
      getCheckoutDelivery({
        locale: serviceLocale,
        cartId: getCartCookie()?.value ?? "",
        zipCode: zipCode ?? "",
      }),
    select: (data) => {
      if (!data?.options?.length) return { ...data, options: [] };
      const options = data.options
        .map((opt) => transformDeliveryOption(opt, locale))
        .filter((opt): opt is DeliveryOption => Boolean(opt));
      return { ...data, options };
    },
    enabled: Boolean(zipCode),
  });
}

/**
 * Get checkout delivery logos from Contentful.
 * Matches Contentful `checkoutDelivery` entries to delivery options by id and
 * returns a map of delivery option id -> logo image.
 */
export function useCheckoutDeliveries() {
  const { contentfulLocale } = useMarket().state.market;

  return useQuery({
    queryKey: ["checkoutDeliveries"],
    queryFn: () => getCheckoutDeliveries({ locale: contentfulLocale }),
    select: (data) => {
      const map = new Map<string, DeliveryOptionImage>();
      for (const entry of data) {
        if (entry?.id && entry.image) map.set(entry.id, entry.image);
      }
      return map;
    },
  });
}

/**
 * Set delivery option
 * This will be called when delivery option changes in the nShift widget
 */
export function useSetCheckoutDelivery(
  callback?: (...args: any) => Promise<any>,
) {
  return useMutation({
    mutationKey: ["deliveryOption"],
    mutationFn: setDeliveryOption,
    onSettled: callback,
  });
}
